import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import RegistrationCitizenship from 'mutation/components/registration-citizenship/form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { transformerRegistrationCitizenshipData } from 'mutation/utils/transformer-registration-citizenship';
import ExitModal from 'mutation/components/shared/exit-modal';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { validationSchema } from 'mutation/constants/registration-citizenship-validation';
import Buttons from 'mutation/components/registration-citizenship/buttons';
import {
  initialRegistrationCitizenship,
  RegistrationCitizenshipFormVM,
} from 'mutation/view-models/registration-citizenship';
import { transformerRequestRegistrationCitizenshipMutation } from 'mutation/utils/transformer-request-registration-citizenship-mutation';
import { StateRegistrationCitizenship } from 'mutation/constants/state-registration-citizenship';
import { transformerRequestRegistrationCitizenshipCancel } from 'mutation/utils/transformer-request-registration-citizenship-cancel';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { RegistrationCitizenshipCrumbs } from 'mutation/constants/registration-citizenship-crumbs';
import PmiTitle from 'shell/components/shared/title';

interface RegistrationCitizenshipPageProps {
  isWithoutBreadcrumbs?: boolean;
  disabledFromAct?: boolean;
  initialValues?: RegistrationCitizenshipFormVM;
  onConfirm?: () => void;
  onCancel?: () => void;
  isNotDisabledCitizenship?: boolean;
}

const RegistrationCitizenshipPageComponent = ({
  isWithoutBreadcrumbs,
  disabledFromAct,
  initialValues,
  onConfirm,
  onCancel,
  isNotDisabledCitizenship,
}: RegistrationCitizenshipPageProps): React.ReactElement => {
  //Modal state
  const [open, setOpen] = useState<boolean>(false);

  const [queryParams] = useSearchParams();
  // query params
  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, RegistrationCitizenshipFormVM>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR: idSchedaSoggettoANPR || undefined,
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
            dataRiferimentoRichiesta:
              filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
          },
        },
      },
      transformer: transformerRegistrationCitizenshipData,
    }
  );

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [responseCitizenship, registerCitizenship] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/registrazione-cittadinanza`, {
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedAnprData = responseCitizenship.data as SaveDataAnpr;

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (
      castedAnprData?.errors?.length &&
      castedAnprData?.errors?.length > 0 &&
      visibility.error === undefined
    ) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const navigate = useNavigate();

  const handleSubmit = useEventCallback((values: RegistrationCitizenshipFormVM) => {
    // To 5008
    if (
      values.newCitizenship?.reasonCitizenship === StateRegistrationCitizenship.OpenLegis ||
      values.newCitizenship?.reasonCitizenship === StateRegistrationCitizenship.ToDPR ||
      values.newCitizenship?.reasonCitizenship === StateRegistrationCitizenship.ChoiceItalian ||
      values.newCitizenship?.reasonCitizenship ===
        StateRegistrationCitizenship.ForeignCitizenship ||
      values.newCitizenship?.reasonCitizenship === StateRegistrationCitizenship.DoubleCitizenship ||
      values.newCitizenship?.reasonCitizenship ===
        StateRegistrationCitizenship.ItalianCitizenshipDP ||
      values.person?.subjectAire?.toUpperCase() !== 'S' // soggetto aire;
    ) {
      const data = transformerRequestRegistrationCitizenshipMutation(values);

      (
        registerCitizenship({
          url: '/mutazione',
          data,
        }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
      ).then((response) => {
        const castedResponse = response as SaveDataAnpr;

        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }
        toast.success('Operazione eseguita con successo');
        if (!Boolean(onConfirm)) {
          navigate('/mutazioni/persona/cittadinanza');
        }
      });
    } else {
      // To 2003
      const data = transformerRequestRegistrationCitizenshipCancel(values);

      (
        registerCitizenship({
          url: '/cancellazione',
          data,
        }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
      ).then((response) => {
        const castedResponse = response as SaveDataAnpr;

        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }
        toast.success('Operazione eseguita con successo');
        if (!Boolean(onConfirm)) {
          navigate('/mutazioni/persona/cittadinanza');
        }
      });
    }

    if (onConfirm) {
      onConfirm();
      return;
    }
  });

  const handleExit = useEventCallback(() => {
    if (onCancel) {
      onCancel();
      return;
    }
    navigate('/mutazioni/persona/cittadinanza');
  });

  const handleOpenModal = useEventCallback(() => {
    setOpen(true);
  });

  const isLoading = !anpr.data || anpr.isLoading;

  return (
    <Fragment>
      {isWithoutBreadcrumbs ? undefined : <Breadcrumbs items={RegistrationCitizenshipCrumbs} />}
      <PmiTitle title="Registrazione Cittadinanza" />
      {!isLoading ? (
        <Fragment>
          <Formik
            validationSchema={validationSchema}
            initialValues={{
              ...initialRegistrationCitizenship,
              ...((anpr.data as RegistrationCitizenshipFormVM) || {}),
              ...(initialValues || {}),
            }}
            onSubmit={handleSubmit}
          >
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <Buttons handleOpenModal={handleOpenModal} />
                </Grid>
                <Grid item xs={12} md={10}>
                  <SearchMessages
                    type={MessageType.Error}
                    title={errorMessage.title || ''}
                    messages={errorMessage.messages || []}
                    visibility={errorMessage.visibility || false}
                    onChangeVisibility={onVisibility}
                  />
                  <SearchMessages
                    type={MessageType.Warning}
                    title={
                      castedAnprData?.warnings?.length === 1
                        ? `C'è 1 warning`
                        : `Ci sono ${castedAnprData?.warnings?.length} warning`
                    }
                    messages={castedAnprData?.warnings || []}
                    visibility={
                      visibility.warning !== undefined
                        ? visibility.warning
                        : (castedAnprData?.warnings || [])?.length > 0
                    }
                    onChangeVisibility={onVisibility}
                  />
                  <RegistrationCitizenship
                    isNationalSearch={nationalSearch}
                    disabled={disabledFromAct}
                    isNotDisabledCitizenship={isNotDisabledCitizenship}
                  />
                </Grid>
              </Grid>
              <ExitModal open={open} onClose={() => setOpen(false)} onConfirm={handleExit} />
            </Form>
          </Formik>
        </Fragment>
      ) : (
        <></>
      )}
    </Fragment>
  );
};

RegistrationCitizenshipPageComponent.displayName = 'Registration Citizenship page component';

const RegistrationCitizenshipPage = memo(RegistrationCitizenshipPageComponent);

export default RegistrationCitizenshipPage;
